import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Phone, Mail, MapPin, ArrowRight, MessageSquare, Calendar,
  Shield, Clock, Star, CheckCircle, Award,
  Quote
} from 'lucide-react';
import { companyInfo, fenceServices } from '../data/mock';
import Header from '../components/layout/Header';
import { useTheme } from '../context/ThemeContext';
import ContactForm from '../components/ContactForm';
import BookingForm from '../components/BookingForm';
import SEO from '../components/SEO';
import { fencingBusinessSchema } from '../components/schemas';
import { generalFencingFAQs } from '../data/faqData';
import { 
  VinylFenceIcon, WoodFenceIcon, ChainLinkIcon, OrnamentalFenceIcon 
} from '../components/icons/FenceIcons';
import CRMPromoSection from '../components/CRMPromoSection';

const FENCE_BOOKING_URL = companyInfo.shared.fenceBookingUrl;

const serviceIcons = {
  'Chain Link Fencing': ChainLinkIcon,
  'Wood Fencing': WoodFenceIcon,
  'Vinyl Fencing': VinylFenceIcon,
  'Ornamental Fencing': OrnamentalFenceIcon,
};

// Default fallback images (replaced by database images when available)
const defaultGalleryItems = [
  { id: 1, title: 'Chain Link Gate with Privacy Slats', image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=400&fit=crop', category: 'chain-link' },
  { id: 2, title: 'Standard Wood Gate', image: 'https://images.unsplash.com/photo-1584284030207-eb240185c5dd?w=600&h=400&fit=crop', category: 'wood' },
  { id: 3, title: '6\' Coastal Cedar Privacy Vinyl Fence', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop', category: 'vinyl' },
  { id: 4, title: '500 Queen St Elnora Project', image: 'https://images.unsplash.com/photo-1565953522043-baea26b83b7e?w=600&h=400&fit=crop', category: 'ornamental' },
];

const reviews = [
  { id: 1, name: 'Dave Morrison', company: 'Morrison Developments', rating: 5, text: 'Central Alberta Fencing installed a chain link perimeter fence around our entire development. Fast, professional, and the quality is outstanding. Highly recommend.', date: '1 month ago' },
  { id: 2, name: 'Karen Peters', company: 'Homeowner - Red Deer', rating: 5, text: 'We went with VEKA vinyl for our backyard and could not be happier. The crew was friendly, efficient, and the fence looks amazing. Zero maintenance!', date: '2 months ago' },
  { id: 3, name: 'Jim Macleod', company: 'Macleod Ranches', rating: 5, text: 'They did our cedar privacy fence and it turned out beautiful. Great communication from start to finish and very fair pricing.', date: '3 months ago' },
];

const fenceTypes = [
  { type: 'Residential', description: 'Privacy, picket, and decorative fencing for homes and acreages.' },
  { type: 'Commercial', description: 'Perimeter security, property boundaries, and site fencing for businesses.' },
  { type: 'Industrial', description: 'Heavy-duty chain link and security fencing for industrial properties.' },
  { type: 'Agricultural', description: 'Farm, ranch, and acreage fencing built to last in rural Alberta.' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FencingPage = () => {
  const info = companyInfo.fence;
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [showContact, setShowContact] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [clientLogos, setClientLogos] = useState([]);
  const [galleryItems, setGalleryItems] = useState(defaultGalleryItems);

  // Fetch gallery images from database (general fencing images)
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/gallery`);
        if (response.ok) {
          const data = await response.json();
          if (data.images && data.images.length > 0) {
            // Filter for fencing-related categories and limit to 4
            const fenceImages = data.images
              .filter(img => ['vinyl', 'wood', 'chain-link', 'ornamental', 'gates'].includes(img.category))
              .slice(0, 4)
              .map(img => ({
                id: img.id,
                title: img.name,
                image: img.url || `${BACKEND_URL}/api/gallery/${img.id}`,
                category: img.category
              }));
            if (fenceImages.length > 0) {
              setGalleryItems(fenceImages);
            }
          }
        }
      } catch { // Silently ignore
      }
    };
    fetchGalleryImages();
  }, []);

  useEffect(() => {
    const fetchLogos = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/logos/fence`);
        const data = await response.json();
        setClientLogos(data);
      } catch { // Silently ignore
      }
    };
    fetchLogos();
  }, []);

  const colors = {
    primary: '#456CAC',
    primaryLight: isDark ? '#6B8DC4' : '#456CAC',
    accent: '#d97706',
    text: isDark ? '#f1f5f9' : '#0f172a',
    textMuted: isDark ? '#94a3b8' : '#64748b',
    bg: isDark ? '#0f172a' : '#f8fafc',
    surface: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '#334155' : '#e2e8f0',
  };
  
  return (
    <div className="min-h-screen font-body transition-colors duration-300" style={{ backgroundColor: colors.bg, color: colors.text }}>
      <SEO 
        title="Fencing Services - Vinyl, Wood, Chain Link & Ornamental"
        description="Central Alberta Fencing Inc provides professional fence supply and installation. Specializing in vinyl, wood, chain link, and ornamental fencing for residential, commercial, and agricultural properties in Red Deer County."
        canonical="/fence"
        image="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=630&fit=crop"
        schema={fencingBusinessSchema}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Fencing Services', path: '/fence' },
        ]}
        faqs={generalFencingFAQs}
        keywords="fence installation, fence supply, Central Alberta fencing, Red Deer fence, vinyl fence, wood fence, chain link, ornamental fence"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&h=1080&fit=crop" 
            alt="5' Highland Universal Top vinyl fence - CAF installation" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ 
            background: isDark 
              ? 'linear-gradient(to right, rgba(15,23,42,0.95), rgba(15,23,42,0.7))' 
              : 'linear-gradient(to right, rgba(255,255,255,0.95), rgba(255,255,255,0.7))'
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6"
                style={{ backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(6,78,59,0.1)', color: colors.primary }}
              >
                Supply & Installation
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Residential & Commercial<br />
                <span style={{ color: colors.primary }}>Fence Experts</span>
              </h1>
              
              <p className="text-lg sm:text-xl leading-relaxed mb-8 max-w-2xl" style={{ color: colors.textMuted }}>
                {info.description}
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 mb-10">
                {[
                  { icon: Shield, text: 'Licensed & Insured' },
                  { icon: Clock, text: 'Free Estimates' },
                  { icon: Star, text: '10+ Years Experience' },
                ].map(badge => (
                  <div 
                    key={badge.text}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all hover:-translate-y-0.5"
                    style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                  >
                    <badge.icon className="w-5 h-5" style={{ color: colors.primary }} />
                    <span className="text-sm font-medium">{badge.text}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowBooking(true)}
                  className="px-8 py-6 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: colors.primary, color: '#fff' }}
                  data-testid="fence-schedule-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Free Estimate
                </Button>
                <Button 
                  onClick={() => setShowContact(true)}
                  variant="outline" 
                  className="px-8 py-6 text-base font-semibold rounded-xl transition-all hover:-translate-y-0.5"
                  style={{ borderColor: colors.border, borderWidth: '2px', color: colors.text, backgroundColor: 'transparent' }}
                  data-testid="fence-quote-btn"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Request Quote
                </Button>
              </div>
            </div>
            
            {/* Hero Stats */}
            <div className="lg:col-span-5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div 
                className="rounded-2xl p-8 shadow-xl"
                style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '1000+', label: 'Fences Installed' },
                    { value: '4', label: 'Fence Types' },
                    { value: '10+', label: 'Years Experience' },
                    { value: '100%', label: 'Satisfaction Rate' },
                  ].map(stat => (
                    <div key={stat.label} className="text-center p-4 rounded-xl" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                      <div className="font-heading text-3xl font-bold mb-1" style={{ color: colors.primary }}>{stat.value}</div>
                      <div className="text-sm" style={{ color: colors.textMuted }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-6 border-y" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              { icon: Shield, text: 'Licensed & Insured' },
              { icon: Star, text: '5-Star Rated' },
              { icon: Clock, text: 'Fast Turnaround' },
              { icon: Award, text: 'Local Experts' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-3">
                <item.icon className="w-5 h-5" style={{ color: colors.primary }} />
                <span className="text-sm font-medium" style={{ color: colors.textMuted }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      {clientLogos.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-bold uppercase tracking-widest mb-8" style={{ color: colors.textMuted }}>
              TRUSTED BY HOMEOWNERS & BUSINESSES ACROSS CENTRAL ALBERTA
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
              {clientLogos.map((logo) => (
                <div 
                  key={logo.id}
                  className="w-32 h-16 rounded-xl flex items-center justify-center p-2 transition-all hover:-translate-y-1"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
                  title={logo.name}
                >
                  <img src={logo.image_url} alt={logo.name} className="max-w-full max-h-full object-contain" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photo Strip - Fence Projects */}
      <section className="py-0 overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          {[
            { url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop', title: 'Vinyl Privacy' },
            { url: 'https://images.unsplash.com/photo-1584284030207-eb240185c5dd?w=600&h=400&fit=crop', title: 'Wood Gates' },
            { url: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=600&h=400&fit=crop', title: 'Chain Link' },
            { url: 'https://images.unsplash.com/photo-1565953522043-baea26b83b7e?w=600&h=400&fit=crop', title: 'Ornamental' },
          ].map((img, idx) => (
            <div key={idx} className="relative h-48 md:h-64 overflow-hidden group">
              <img 
                src={img.url} 
                alt={img.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-medium">{img.title}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24" style={{ backgroundColor: colors.bg }} data-testid="fence-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.primary }}>
              Our Products
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Our Fencing Products
            </h2>
            <p className="text-lg" style={{ color: colors.textMuted }}>
              Quality supply and professional installation for every fencing need
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {fenceServices.map((service, index) => {
              const IconComponent = serviceIcons[service.title] || VinylFenceIcon;
              return (
                <Link
                  key={service.id}
                  to={`/fence/${service.slug}`}
                  className="group rounded-2xl p-8 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl flex flex-col h-full"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}`, animationDelay: `${index * 0.1}s` }}
                  data-testid={`fence-service-${service.slug}`}
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-colors"
                    style={{ backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(6,78,59,0.1)' }}
                  >
                    <IconComponent className="w-7 h-7" style={{ color: colors.primary }} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-sm leading-relaxed mb-6 flex-grow" style={{ color: colors.textMuted }}>
                    {service.shortDescription}
                  </p>
                  <span className="inline-flex items-center text-sm font-semibold group-hover:gap-3 transition-all mt-auto" style={{ color: colors.primary }}>
                    Learn More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fence Types */}
      <section className="py-24" style={{ backgroundColor: colors.surface }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.primary }}>
              Applications
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              We Serve All Sectors
            </h2>
            <p className="text-lg" style={{ color: colors.textMuted }}>
              From backyard fences to industrial perimeters, we do it all
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {fenceTypes.map((item) => (
              <div
                key={item.type}
                className="rounded-2xl p-6 transition-all hover:-translate-y-1"
                style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(6,78,59,0.1)' }}
                >
                  <CheckCircle className="w-6 h-6" style={{ color: colors.primary }} />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">{item.type}</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-24" style={{ backgroundColor: colors.bg }} data-testid="fence-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.primary }}>
              Our Work
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Installation Gallery
            </h2>
            <p className="text-lg" style={{ color: colors.textMuted }}>
              See our quality fencing installations across Central Alberta
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className="group rounded-2xl overflow-hidden cursor-pointer transition-all hover:-translate-y-2 hover:shadow-xl"
                style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h4 className="font-heading font-semibold">{item.title}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Property Developers */}
      <section className="py-16" style={{ backgroundColor: colors.bg }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: colors.accent }}>
                Commercial Clients
              </p>
              <h3 className="font-heading text-2xl font-semibold mb-2">
                Trusted By Property Developers
              </h3>
              <p className="text-sm" style={{ color: colors.textMuted }}>
                We provide fencing solutions for residential developments, commercial properties, and subdivisions across Central Alberta.
              </p>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <a 
                href="https://www.melcor.ca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="group"
              >
                <div 
                  className="px-8 py-4 rounded-xl transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-1"
                  style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
                >
                  <img 
                    src="/logos/suppliers/melcor.jpg" 
                    alt="Melcor Developments"
                    className="h-10 w-auto object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24" style={{ backgroundColor: colors.surface }} data-testid="fence-reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.primary }}>
              Testimonials
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              What Our Clients Say
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-5 h-5 text-amber-500 fill-amber-500" />
                ))}
              </div>
              <span style={{ color: colors.textMuted }}>5.0 Average Rating</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="rounded-2xl p-8 transition-all hover:-translate-y-1"
                style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
              >
                <Quote className="w-10 h-10 mb-4" style={{ color: isDark ? 'rgba(16,185,129,0.3)' : 'rgba(6,78,59,0.2)' }} />
                <p className="mb-6 leading-relaxed" style={{ color: colors.textMuted }}>
                  &ldquo;{review.text}&rdquo;
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm" style={{ color: colors.textMuted }}>{review.company}</p>
                  </div>
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <a 
              href="https://www.google.com/search?q=Central+Alberta+Fencing+Red+Deer+reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold transition-colors"
              style={{ color: colors.primary }}
            >
              See all reviews on Google
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Suppliers/Partners Section */}
      <section className="py-24" style={{ backgroundColor: colors.surface }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.accent }}>
              Authorized Dealer
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Premium Suppliers, Quality Guaranteed
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
              We partner with industry leaders to bring you the best materials
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-5">
            {[
              { name: 'VEKA', description: 'Premium Vinyl Fencing', specialty: 'Lifetime Warranty Vinyl', website: 'https://vekaolp.com/vekafence', logo: '/logos/suppliers/veka.png', brandColor: '#0066B3' },
              { name: 'Catalyst Fence Solutions', description: 'Designer Vinyl Collections', specialty: 'Woodgrain Finishes', website: 'https://catalystfence.com', logo: '/logos/suppliers/catalyst-fence-solutions.jpg', brandColor: '#2A9D8F' },
              { name: 'Atlas Hardware', description: 'Premium Gate Hardware', specialty: 'Commercial Grade', website: 'https://atlashardware.ca', logo: '/logos/suppliers/atlas-hardware.png', brandColor: '#C9A227' },
              { name: 'Rona', description: 'Pressure Treated Lumber', specialty: 'MicroPro Sienna', website: 'https://www.rona.ca', logo: '/logos/suppliers/rona.jpg', brandColor: '#003366' },
              { name: 'Direct Fencing Supply', description: 'Ornamental Iron & Aluminum', specialty: 'Made in Canada', website: 'https://directfencingsupply.ca', logo: '/logos/suppliers/dfs.png', brandColor: '#C41E3A' },
              { name: 'Malden Metals', description: 'Steel & Metal Fabrication', specialty: 'Custom Metalwork', website: 'https://maldenmetals.com', logo: '/logos/suppliers/malden-metals.png', brandColor: '#8B2332' },
              { name: 'Ameristar', description: 'Commercial Security Fencing', specialty: '20yr Warranty', website: 'https://www.ameristarperimeter.com', logo: '/logos/suppliers/ameristar.jpg', brandColor: '#1A1A1A' },
            ].map((supplier) => (
              <a
                key={supplier.name}
                href={supplier.website}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
              >
                <div 
                  className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-4 overflow-hidden transition-all duration-300 group-hover:shadow-lg"
                  style={{ backgroundColor: '#fff', border: `1px solid ${colors.borderLight || colors.border}` }}
                >
                  <img 
                    src={supplier.logo} 
                    alt={`${supplier.name} logo`} 
                    className="w-16 h-16 object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="font-semibold text-sm mb-1 transition-colors group-hover:text-[#456CAC]">{supplier.name}</h3>
                <p className="text-xs mb-2" style={{ color: colors.textMuted }}>{supplier.description}</p>
                <span 
                  className="inline-block text-xs font-medium px-2 py-1 rounded-full"
                  style={{ backgroundColor: `${supplier.brandColor}15`, color: supplier.brandColor }}
                >
                  {supplier.specialty}
                </span>
              </a>
            ))}
          </div>

          {/* Safety Partners Row */}
          <div className="mt-12 pt-10 border-t" style={{ borderColor: colors.border }}>
            <p className="text-center text-sm font-medium mb-6" style={{ color: colors.textMuted }}>
              Safety Partners
            </p>
            <div className="flex flex-wrap justify-center items-center gap-10">
              {[
                { name: 'WCB Alberta', logo: '/logos/suppliers/wcb-alberta.jpg', website: 'https://www.wcb.ab.ca' },
                { name: 'Alberta One Call', logo: '/logos/badges/alberta-one-call.jpg', website: 'https://albertaonecall.com' },
              ].map((cert) => (
                <a
                  key={cert.name}
                  href={cert.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center transition-all duration-300 hover:scale-105"
                  title={cert.name}
                >
                  <div 
                    className="w-24 h-20 rounded-lg flex items-center justify-center overflow-hidden transition-shadow duration-300 group-hover:shadow-md p-2"
                    style={{ backgroundColor: '#fff', border: `1px solid ${colors.borderLight || colors.border}` }}
                  >
                    <img 
                      src={cert.logo} 
                      alt={cert.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs mt-2 text-center font-medium" style={{ color: colors.textMuted }}>
                    {cert.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CRM Portal Section */}
      <CRMPromoSection variant="fence" />

      {/* Service Area Section */}
      <section className="py-24" style={{ backgroundColor: colors.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: colors.primary }}>
                Coverage
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-6">Service Area</h2>
              <p className="mb-8" style={{ color: colors.textMuted }}>
                We supply and install fencing throughout Central Alberta, including:
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['Red Deer', 'Lacombe', 'Blackfalds', 'Sylvan Lake', 'Ponoka', 'Innisfail', 'Penhold', 'Red Deer County'].map((city) => (
                  <div key={city} className="flex items-center gap-3">
                    <MapPin className="w-5 h-5" style={{ color: colors.primary }} />
                    <span style={{ color: colors.textMuted }}>{city}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm" style={{ color: colors.textMuted }}>
                Don&apos;t see your area? Contact us - we may still be able to help!
              </p>
            </div>
            
            <div 
              className="rounded-2xl overflow-hidden h-80"
              style={{ border: `1px solid ${colors.border}` }}
            >
              <iframe
                src="https://www.google.com/maps?q=Red+Deer+County,+Alberta,+Canada&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: isDark ? 'invert(90%) hue-rotate(180deg)' : 'none' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Fencing Service Area Map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges, Financing & Payment Section */}
      <section className="py-16" style={{ backgroundColor: colors.surface }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Awards & Memberships Row */}
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Award */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <img 
                  src="/logos/badges/consumer-choice-award.png" 
                  alt="Consumer Choice Award - Business Excellence"
                  className="h-20 w-auto object-contain"
                  loading="lazy"
                />
              </div>
              <p className="text-xs font-semibold">Business Excellence</p>
            </div>

            {/* Chamber of Commerce */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <img 
                  src="/logos/badges/red-deer-chamber.jpg" 
                  alt="Red Deer & District Chamber of Commerce"
                  className="h-20 w-auto object-contain rounded-lg"
                  loading="lazy"
                />
              </div>
              <p className="text-xs font-semibold">Chamber Member</p>
            </div>

            {/* Red Deer Home Show */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div 
                  className="h-20 w-36 flex items-center justify-center rounded-lg px-3"
                  style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
                >
                  <img 
                    src="/logos/badges/red-deer-home-show.png" 
                    alt="Red Deer Home Show"
                    className="h-16 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
              <p className="text-xs font-semibold">Home Show Exhibitor</p>
            </div>

            {/* Payment Options */}
            <div className="text-center">
              <div className="flex justify-center items-center gap-3 h-20 mb-3">
                <img 
                  src="/logos/badges/visa.png" 
                  alt="Visa Accepted"
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                />
                <img 
                  src="/logos/badges/mastercard.png" 
                  alt="Mastercard Accepted"
                  className="h-8 w-auto object-contain"
                  loading="lazy"
                />
                <img 
                  src="/logos/badges/financeit.jpg" 
                  alt="FinanceIt - Easy Financing"
                  className="h-8 w-auto object-contain rounded"
                  loading="lazy"
                />
              </div>
              <p className="text-xs font-semibold">Financing Available</p>
            </div>
          </div>

          {/* Proudly Serving Row */}
          <div className="pt-8 border-t" style={{ borderColor: colors.border }}>
            <p className="text-center text-xs font-bold uppercase tracking-widest mb-6" style={{ color: colors.textMuted }}>
              Proudly Serving
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6 lg:gap-10">
              {[
                { name: 'City of Red Deer', logo: '/logos/red-deer.svg', slug: 'red-deer' },
                { name: 'Red Deer County', logo: '/logos/red-deer-county.png', slug: 'red-deer' },
                { name: 'Lacombe', logo: '/logos/lacombe.png', slug: 'lacombe' },
                { name: 'Blackfalds', logo: '/logos/blackfalds.png', slug: 'blackfalds' },
                { name: 'Sylvan Lake', logo: '/logos/sylvan-lake.svg', slug: 'sylvan-lake' },
                { name: 'Ponoka', logo: '/logos/ponoka.svg', slug: 'ponoka' },
                { name: 'Innisfail', logo: '/logos/innisfail.png', slug: 'innisfail' },
                { name: 'Penhold', logo: '/logos/penhold.png', slug: 'penhold' },
                { name: 'Rocky Mountain House', logo: '/logos/rocky-mountain-house.png', slug: 'rocky-mountain-house' },
              ].map((city) => (
                <Link 
                  key={city.name}
                  to={`/fence/${city.slug}`}
                  className="flex flex-col items-center transition-all hover:scale-105"
                  title={`Fence installation in ${city.name}`}
                >
                  <div 
                    className="w-14 h-14 rounded-lg flex items-center justify-center overflow-hidden p-1"
                    style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <img 
                      src={city.logo} 
                      alt={`${city.name} official logo`}
                      className="max-w-full max-h-full object-contain opacity-70 hover:opacity-100 transition-opacity"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-[10px] mt-1 text-center hover:underline" style={{ color: colors.textMuted }}>
                    {city.name.replace('City of ', '')}
                  </span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6">
              <Link to="/fence/developments" className="text-sm font-medium hover:underline" style={{ color: colors.primary }}>
                View New Developments & Subdivisions →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" style={{ backgroundColor: colors.primary }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-white">
            Ready for Your New Fence?
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto text-white/80">
            Contact us today for a free on-site estimate. We&apos;ll help you choose the right fence for your property and budget.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${info.phone.replace(/[^0-9]/g, '')}`}>
              <Button className="px-8 py-6 text-base font-semibold rounded-xl bg-white hover:bg-gray-100 transition-all hover:-translate-y-0.5" style={{ color: colors.primary }} data-testid="fence-cta-phone">
                <Phone className="w-5 h-5 mr-2" />{info.phone}
              </Button>
            </a>
            <Button onClick={() => setShowBooking(true)} className="px-8 py-6 text-base font-semibold rounded-xl bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 transition-all hover:-translate-y-0.5" data-testid="fence-cta-booking">
              <Calendar className="w-5 h-5 mr-2" />Book Free Estimate
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t" style={{ backgroundColor: colors.bg, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-1">
              <h3 className="font-heading text-lg font-semibold mb-4">{info.name}</h3>
              <p className="text-sm mb-4" style={{ color: colors.textMuted }}>{info.tagline}</p>
              <div className="space-y-3">
                <a href={`tel:${info.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-sm transition-colors hover:opacity-80" style={{ color: colors.textMuted }}>
                  <Phone className="w-4 h-4" style={{ color: colors.primary }} />{info.phone}
                </a>
                <a href={`mailto:${info.email}`} className="flex items-center gap-3 text-sm transition-colors hover:opacity-80" style={{ color: colors.textMuted }}>
                  <Mail className="w-4 h-4" style={{ color: colors.primary }} />{info.email}
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-4">Products</h3>
              <ul className="space-y-3 text-sm" style={{ color: colors.textMuted }}>
                {fenceServices.map(s => (
                  <li key={s.id}><Link to={`/fence/${s.slug}`} className="transition-colors hover:opacity-80">{s.title}</Link></li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-3 text-sm" style={{ color: colors.textMuted }}>
                <li><Link to="/fence/developments" className="transition-colors hover:opacity-80">New Developments</Link></li>
                <li><Link to="/fence/bylaws" className="transition-colors hover:opacity-80">Municipal Bylaws</Link></li>
                <li><Link to="/fence/gallery" className="transition-colors hover:opacity-80">Project Gallery</Link></li>
                <li><Link to="/fence/faq" className="transition-colors hover:opacity-80">FAQ</Link></li>
                <li><button onClick={() => setShowBooking(true)} className="transition-colors hover:opacity-80">Book Estimate</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading text-lg font-semibold mb-4">Business Hours</h3>
              <div className="space-y-2 text-sm" style={{ color: colors.textMuted }}>
                <p>Monday - Friday: 8:00 AM - 4:30 PM</p>
                <p>Saturday - Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: colors.border }}>
            <p className="text-sm" style={{ color: colors.textMuted }}>&copy; {new Date().getFullYear()} {info.name}. All rights reserved.</p>
            <p className="text-sm" style={{ color: colors.textMuted }}>Part of CAF Group of Companies</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowContact(false)} />
          <div className="relative z-10 w-full max-w-xl animate-fade-in-up">
            <ContactForm serviceType="fence" serviceName="Fencing Services" onClose={() => setShowContact(false)} />
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="max-w-2xl w-full my-8">
            <BookingForm 
              serviceType="fence" 
              serviceName="Fencing Services"
              onClose={() => setShowBooking(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FencingPage;
